import {
  matchesFilters,
  compareRows,
  parseTsv,
  type FilterState,
  type SortItem,
} from './ExcelEditableTable'

export type FreeColumn = {
  key: string
  label: string
  type: 'text' | 'number' | 'currency'
  core: boolean
  readOnly?: boolean
  width?: number
}

export type FreeRow = Record<string, string | number | null> & { id: string }

export const CORE_COLUMNS: readonly FreeColumn[] = [
  { key: 'name', label: 'Tên hàng', type: 'text', core: true },
  { key: 'uom', label: 'ĐVT', type: 'text', core: true },
  { key: 'qty', label: 'Số lượng', type: 'number', core: true },
  { key: 'unit_price', label: 'Đơn giá', type: 'currency', core: true },
  { key: 'line_total', label: 'Thành tiền', type: 'currency', core: true, readOnly: true },
  { key: 'note', label: 'Ghi chú', type: 'text', core: true },
]

export const CORE_KEYS = new Set(CORE_COLUMNS.map((c) => c.key))

export function isCoreColumn(key: string): boolean {
  return CORE_KEYS.has(key)
}

export const newId = (): string => Math.random().toString(36).slice(2, 10)

export function newCustomColumn(
  label: string = 'Cột mới',
  type: 'text' | 'number' | 'currency' = 'text',
): FreeColumn {
  return {
    key: `c_${newId()}`,
    label,
    type,
    core: false,
  }
}

/**
 * Normalizes column list: ensures all 6 core columns exist,
 * preserves core flags / readOnly on line_total, and guarantees
 * `note` is ALWAYS the last column.
 */
export function normalizeColumns(columns: FreeColumn[]): FreeColumn[] {
  const result: FreeColumn[] = []
  const seenKeys = new Set<string>()

  // 1. Process provided columns (except note, which will be placed last)
  for (const col of columns) {
    if (seenKeys.has(col.key)) continue
    seenKeys.add(col.key)
    if (col.key === 'note') continue // defer note to the end

    if (CORE_KEYS.has(col.key)) {
      result.push({
        ...col,
        core: true,
        readOnly: col.key === 'line_total' ? true : col.readOnly,
      })
    } else {
      result.push(col)
    }
  }

  // 2. Ensure all required core columns (except note) are present
  for (const core of CORE_COLUMNS) {
    if (core.key === 'note') continue
    if (!seenKeys.has(core.key)) {
      result.push({ ...core })
      seenKeys.add(core.key)
    }
  }

  // 3. Find or create the note column and append it at the very end
  const userNote = columns.find((c) => c.key === 'note')
  const noteCol: FreeColumn = userNote
    ? { ...userNote, core: true }
    : { key: 'note', label: 'Ghi chú', type: 'text', core: true }

  result.push(noteCol)
  return result
}

/**
 * Robust Vietnamese & international number parsing.
 * Supports:
 * - VN format: 1.200.000 or 1.200.000,50
 * - US format: 1,200,000 or 1,200,000.50
 * - Spaces: 1 200 000
 * - Decimals: 12.5 or 12,5
 * - Plain: 1200000
 * - Negative numbers: -1.200.000
 */
export function parseNumberVi(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }
  if (value === null || value === undefined) return null

  const str = String(value)
    .trim()
    .replace(/[\s\u00a0₫đ$]/gi, '')
  if (str === '') return null
  if (!/^[+-]?[\d.,]+$/.test(str)) return null

  const isNegative = str.startsWith('-')
  const unsigned = str.replace(/^[+-]/, '')

  const dotCount = (unsigned.match(/\./g) || []).length
  const commaCount = (unsigned.match(/,/g) || []).length

  let normalized: string

  if (dotCount > 0 && commaCount > 0) {
    const lastDot = unsigned.lastIndexOf('.')
    const lastComma = unsigned.lastIndexOf(',')
    if (lastComma > lastDot) {
      normalized = unsigned.replace(/\./g, '').replace(',', '.')
    } else {
      normalized = unsigned.replace(/,/g, '')
    }
  } else if (commaCount > 1) {
    normalized = unsigned.replace(/,/g, '')
  } else if (dotCount > 1) {
    normalized = unsigned.replace(/\./g, '')
  } else if (commaCount === 1) {
    const parts = unsigned.split(',')
    if (parts[1].length === 3 && parts[0].length >= 1 && parts[0].length <= 3 && parseInt(parts[0], 10) >= 1) {
      normalized = unsigned.replace(/,/g, '')
    } else {
      normalized = unsigned.replace(',', '.')
    }
  } else if (dotCount === 1) {
    const parts = unsigned.split('.')
    if (parts[1].length === 3 && parts[0].length >= 1 && parts[0].length <= 3 && parseInt(parts[0], 10) >= 1) {
      normalized = unsigned.replace(/\./g, '')
    } else {
      normalized = unsigned
    }
  } else {
    normalized = unsigned
  }

  const num = Number(normalized)
  if (!Number.isFinite(num)) return null
  return isNegative ? -num : num
}

/**
 * Normalizes a raw numeric value from server (PostgreSQL numeric string, number, null/undefined)
 * into a finite JavaScript number, or null if null/undefined/empty.
 * Server numeric strings ALWAYS use standard decimal dot: e.g. "100.0000", "12345678.50".
 */
export function normalizeServerNumber(val: unknown): number | null {
  if (val === null || val === undefined || val === '') return null
  if (typeof val === 'number') return Number.isFinite(val) ? val : null
  const str = String(val).trim()
  if (str === '') return null
  const num = Number(str)
  return Number.isFinite(num) ? num : null
}

/**
 * Normalizes a line item from server (raw NUMERIC strings like "100.0000", "12345678.50")
 * into numbers before populating FreeSheetTable rows.
 */
export function normalizeFreeLine(line: any, columns?: readonly FreeColumn[] | FreeColumn[]): FreeRow {
  const qty = normalizeServerNumber(line.qty) ?? 0
  const unitPrice = normalizeServerNumber(line.unit_price)
  const lineTotal = line.line_total != null
    ? (normalizeServerNumber(line.line_total) ?? 0)
    : (unitPrice != null ? Math.round((qty * unitPrice + Number.EPSILON) * 100) / 100 : 0)

  const extra: Record<string, unknown> = { ...(line.extra || {}) }
  if (columns && Array.isArray(columns)) {
    for (const col of columns) {
      if (!col.core && (col.type === 'number' || col.type === 'currency')) {
        if (extra[col.key] != null) {
          extra[col.key] = normalizeServerNumber(extra[col.key])
        }
      }
    }
  }

  return {
    id: line.id,
    name: line.name ?? '',
    uom: line.uom ?? '',
    qty,
    unit_price: unitPrice,
    line_total: lineTotal,
    note: line.note ?? '',
    ...extra,
  }
}

/**
 * Inserts a new column at the specified index.
 * Prevents inserting after the `note` column (note is always last).
 */
export function insertColumn(
  columns: FreeColumn[],
  index: number,
  column: FreeColumn,
): FreeColumn[] {
  const norm = normalizeColumns(columns)
  const noteIdx = norm.findIndex((c) => c.key === 'note')
  const maxPos = noteIdx !== -1 ? noteIdx : norm.length
  const at = Math.max(0, Math.min(index, maxPos))
  const next = [...norm.slice(0, at), column, ...norm.slice(at)]
  return normalizeColumns(next)
}

/**
 * Removes a column by key.
 * Core columns cannot be removed; returns original columns unchanged.
 */
export function removeColumn(columns: FreeColumn[], key: string): FreeColumn[] {
  const target = columns.find((c) => c.key === key)
  if (!target || target.core || isCoreColumn(key)) {
    return columns
  }
  return normalizeColumns(columns.filter((c) => c.key !== key))
}

/**
 * Moves a column from index `from` to index `to`.
 * Note column cannot be moved, and no column can be moved past note.
 */
export function moveColumn(
  columns: FreeColumn[],
  from: number,
  to: number,
): FreeColumn[] {
  const norm = normalizeColumns(columns)
  if (from < 0 || from >= norm.length) {
    return norm
  }

  // Moving note is forbidden
  if (norm[from].key === 'note') {
    return norm
  }

  const noteIdx = norm.findIndex((c) => c.key === 'note')
  const maxPos = noteIdx !== -1 ? noteIdx - 1 : norm.length - 1
  const clampedTo = Math.max(0, Math.min(to, maxPos))
  if (from === clampedTo) return norm

  const next = [...norm]
  const [moved] = next.splice(from, 1)
  next.splice(clampedTo, 0, moved)
  return normalizeColumns(next)
}

/**
 * Renames a column label.
 * Technical labels of core columns cannot be renamed.
 */
export function renameColumn(
  columns: FreeColumn[],
  key: string,
  label: string,
): FreeColumn[] {
  const target = columns.find((c) => c.key === key)
  if (!target || !label || !label.trim()) {
    return columns
  }
  return columns.map((c) => (c.key === key ? { ...c, label: label.trim() } : c))
}

/**
 * Inserts a row at `index`.
 */
export function insertRow(rows: FreeRow[], index: number, row: FreeRow): FreeRow[] {
  const at = Math.max(0, Math.min(index, rows.length))
  return [...rows.slice(0, at), row, ...rows.slice(at)]
}

/**
 * Removes a row by `index`.
 */
export function removeRow(rows: FreeRow[], index: number): FreeRow[] {
  if (index < 0 || index >= rows.length) return rows
  return rows.filter((_, i) => i !== index)
}

/**
 * Moves a row from `from` index to `to` index.
 */
export function moveRow(rows: FreeRow[], from: number, to: number): FreeRow[] {
  if (
    from < 0 ||
    to < 0 ||
    from >= rows.length ||
    to >= rows.length ||
    from === to
  ) {
    return rows
  }
  const next = [...rows]
  const [row] = next.splice(from, 1)
  next.splice(to, 0, row)
  return next
}

/**
 * Computes sum for a column, avoiding floating point precision errors.
 */
export function sumColumn(
  rows: FreeRow[],
  key: string,
  compute?: (row: FreeRow) => FreeRow,
): number {
  let total = 0
  for (const row of rows) {
    const target = compute ? compute(row) : row
    const val = parseNumberVi(target[key])
    if (val !== null && Number.isFinite(val)) {
      total += val
    }
  }
  return Math.round(total * 10000) / 10000
}

export type VisibleRowItem = {
  row: FreeRow
  computed: FreeRow
  originalIndex: number
}

/**
 * Computes visible rows mapped to originalIndex, applying active filters and sort.
 */
export function computeVisibleFreeRows({
  rows,
  columns,
  filters = {},
  sort = [],
  computeRow,
}: {
  rows: FreeRow[]
  columns: FreeColumn[]
  filters?: FilterState
  sort?: SortItem[]
  computeRow?: (row: FreeRow) => FreeRow
}): VisibleRowItem[] {
  const normCols = normalizeColumns(columns)
  const list = rows.map((row, originalIndex) => {
    const computed = computeRow ? computeRow(row) : row
    return { row, computed, originalIndex }
  })

  let filtered = list
  if (Object.keys(filters).length > 0) {
    filtered = filtered.filter(({ computed }) =>
      matchesFilters(computed, filters, normCols as never),
    )
  }

  if (sort.length > 0) {
    filtered = [...filtered].sort((a, b) =>
      compareRows(a.computed, b.computed, sort, normCols as never),
    )
  }

  return filtered
}

/**
 * Applies 2D TSV text to rows starting from target visible cell (startR, startC).
 * Auto-appends rows when pasting beyond the last row.
 * Normalizes numbers and preserves raw text on invalid entries.
 */
export function applyPasteToRows({
  tsv,
  rows,
  columns,
  visibleRows,
  startR = 0,
  startC = 0,
  computeRow,
}: {
  tsv: string
  rows: FreeRow[]
  columns: FreeColumn[]
  visibleRows?: Array<{ row: FreeRow; originalIndex: number }>
  startR?: number
  startC?: number
  computeRow?: (row: FreeRow) => FreeRow
}): FreeRow[] {
  const grid = parseTsv(tsv)
  if (grid.length === 0) return rows

  const normCols = normalizeColumns(columns)
  const vRows =
    visibleRows ??
    rows.map((row, originalIndex) => ({ row, originalIndex }))

  const nextRows = rows.map((r) => ({ ...r }))

  grid.forEach((lineCells, dr) => {
    const vIdx = startR + dr
    let targetOrigIdx: number

    if (vIdx < vRows.length) {
      targetOrigIdx = vRows[vIdx].originalIndex
    } else {
      targetOrigIdx = nextRows.length
      nextRows.push({ id: `row_${newId()}` })
    }

    lineCells.forEach((cellRaw, dc) => {
      const colIdx = startC + dc
      if (colIdx >= normCols.length) return
      const col = normCols[colIdx]
      if (!col || col.readOnly || col.key === 'line_total') return

      const trimmed = cellRaw.trim()
      let valToSet: string | number | null = trimmed
      if (col.type === 'number' || col.type === 'currency') {
        const parsed = parseNumberVi(trimmed)
        valToSet = parsed !== null ? parsed : trimmed
      }

      nextRows[targetOrigIdx] = {
        ...nextRows[targetOrigIdx],
        [col.key]: valToSet,
      }
    })

    if (computeRow) {
      nextRows[targetOrigIdx] = computeRow(nextRows[targetOrigIdx])
    }
  })

  return nextRows
}

/**
 * Extracts rectangular cell range selection into TSV format for clipboard copying.
 */
export function copySelectionToTsv({
  rows,
  columns,
  visibleRows,
  r0,
  r1,
  c0,
  c1,
  computeRow,
}: {
  rows: FreeRow[]
  columns: FreeColumn[]
  visibleRows?: Array<{ row: FreeRow; originalIndex: number }>
  r0: number
  r1: number
  c0: number
  c1: number
  computeRow?: (row: FreeRow) => FreeRow
}): string {
  const normCols = normalizeColumns(columns)
  const vRows =
    visibleRows ??
    rows.map((row, originalIndex) => ({ row, originalIndex }))

  const rMin = Math.max(0, Math.min(r0, r1))
  const rMax = Math.min(vRows.length - 1, Math.max(r0, r1))
  const cMin = Math.max(0, Math.min(c0, c1))
  const cMax = Math.min(normCols.length - 1, Math.max(c0, c1))

  if (vRows.length === 0 || rMin > rMax || cMin > cMax) return ''

  const lines: string[] = []
  for (let r = rMin; r <= rMax; r++) {
    const item = vRows[r]
    if (!item) continue
    const targetRow = computeRow ? computeRow(item.row) : item.row
    const lineCells: string[] = []

    for (let c = cMin; c <= cMax; c++) {
      const col = normCols[c]
      if (!col) continue
      const val = targetRow[col.key]
      lineCells.push(val != null ? String(val) : '')
    }
    lines.push(lineCells.join('\t'))
  }

  return lines.join('\r\n')
}

/**
 * Clears editable cells in rectangular selection range.
 * Preserves core columns and readOnly columns (per spec: "không xóa ô readOnly, không xóa cột lõi").
 */
export function clearCellsInSelection({
  rows,
  columns,
  visibleRows,
  r0,
  r1,
  c0,
  c1,
}: {
  rows: FreeRow[]
  columns: FreeColumn[]
  visibleRows?: Array<{ row: FreeRow; originalIndex: number }>
  r0: number
  r1: number
  c0: number
  c1: number
}): FreeRow[] {
  const normCols = normalizeColumns(columns)
  const vRows =
    visibleRows ??
    rows.map((row, originalIndex) => ({ row, originalIndex }))

  const rMin = Math.max(0, Math.min(r0, r1))
  const rMax = Math.min(vRows.length - 1, Math.max(r0, r1))
  const cMin = Math.max(0, Math.min(c0, c1))
  const cMax = Math.min(normCols.length - 1, Math.max(c0, c1))

  const targetIndices = new Set<number>()
  for (let r = rMin; r <= rMax; r++) {
    if (vRows[r]) targetIndices.add(vRows[r].originalIndex)
  }

  return rows.map((row, idx) => {
    if (!targetIndices.has(idx)) return row
    const updated = { ...row }
    for (let c = cMin; c <= cMax; c++) {
      const col = normCols[c]
      // Skip readOnly and core columns
      if (
        !col ||
        col.core ||
        isCoreColumn(col.key) ||
        col.readOnly ||
        col.key === 'line_total'
      ) {
        continue
      }
      updated[col.key] = ''
    }
    return updated
  })
}

/**
 * Dispatches context menu actions for column and row manipulation.
 */
export function dispatchMenuAction({
  action,
  rows,
  columns,
  visibleRows,
  r,
  c,
  newLabel,
}: {
  action:
    | 'insert-col-left'
    | 'insert-col-right'
    | 'insert-row-above'
    | 'insert-row-below'
    | 'rename-col'
    | 'delete-col'
    | 'delete-row'
    | 'move-row-up'
    | 'move-row-down'
  rows: FreeRow[]
  columns: FreeColumn[]
  visibleRows?: Array<{ row: FreeRow; originalIndex: number }>
  r: number
  c: number
  newLabel?: string
}): { rows: FreeRow[]; columns: FreeColumn[] } {
  const normCols = normalizeColumns(columns)
  const vRows =
    visibleRows ??
    rows.map((row, originalIndex) => ({ row, originalIndex }))

  const targetItem = vRows[r]
  const origIdx = targetItem?.originalIndex
  const col = normCols[c]

  if (action === 'insert-col-left') {
    const newCol: FreeColumn = {
      key: `c_${newId()}`,
      label: 'Cột mới',
      type: 'text',
      core: false,
    }
    return { rows, columns: insertColumn(normCols, c, newCol) }
  }

  if (action === 'insert-col-right') {
    if (!col || col.key === 'note') {
      return { rows, columns: normCols }
    }
    const newCol: FreeColumn = {
      key: `c_${newId()}`,
      label: 'Cột mới',
      type: 'text',
      core: false,
    }
    return { rows, columns: insertColumn(normCols, c + 1, newCol) }
  }

  if (action === 'insert-row-above') {
    if (origIdx == null) return { rows, columns: normCols }
    const newRow: FreeRow = { id: `row_${newId()}` }
    return { rows: insertRow(rows, origIdx, newRow), columns: normCols }
  }

  if (action === 'insert-row-below') {
    if (origIdx == null) return { rows, columns: normCols }
    const newRow: FreeRow = { id: `row_${newId()}` }
    return { rows: insertRow(rows, origIdx + 1, newRow), columns: normCols }
  }

  if (action === 'rename-col') {
    if (!col || !newLabel) {
      return { rows, columns: normCols }
    }
    return { rows, columns: renameColumn(normCols, col.key, newLabel) }
  }

  if (action === 'delete-col') {
    if (!col || col.core || isCoreColumn(col.key)) {
      return { rows, columns: normCols }
    }
    return { rows, columns: removeColumn(normCols, col.key) }
  }

  if (action === 'delete-row') {
    if (origIdx == null) return { rows, columns: normCols }
    return { rows: removeRow(rows, origIdx), columns: normCols }
  }

  if (action === 'move-row-up') {
    if (origIdx == null || r <= 0 || !vRows[r - 1]) {
      return { rows, columns: normCols }
    }
    const prevOrigIdx = vRows[r - 1].originalIndex
    return { rows: moveRow(rows, origIdx, prevOrigIdx), columns: normCols }
  }

  if (action === 'move-row-down') {
    if (origIdx == null || r >= vRows.length - 1 || !vRows[r + 1]) {
      return { rows, columns: normCols }
    }
    const nextOrigIdx = vRows[r + 1].originalIndex
    return { rows: moveRow(rows, origIdx, nextOrigIdx), columns: normCols }
  }

  return { rows, columns: normCols }
}

/**
 * Tên chi tiết của dòng PO tự do: tên hàng + giá trị các cột người dùng tự thêm, nối " - ".
 * Cùng quy tắc với tên chi tiết vật tư kho (buildStockMaterialName): bỏ ô trống, bỏ phần đã nằm trong tên.
 */
export function freeLineDetailName(name: string, extra: Record<string, unknown>, columns: readonly FreeColumn[]): string {
  const base = String(name ?? '').trim()
  const parts: string[] = []
  for (const col of columns) {
    if (col.core) continue
    const value = String(extra[col.key] ?? '').trim()
    if (!value) continue
    if (base.toLocaleLowerCase('vi').includes(value.toLocaleLowerCase('vi'))) continue
    if (parts.some((p) => p.toLocaleLowerCase('vi') === value.toLocaleLowerCase('vi'))) continue
    parts.push(value)
  }
  return [base, ...parts].filter(Boolean).join(' - ')
}
